import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnInit } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { handleResponse } from 'src/functions/http-handler';

const SITE_TYPE = 'SITE_TYPE';
const GROUP_CODE = 'PGM_GROUP';
const TYPE_CODE = 'PGM_TYPE';
const NOTICE_CATEGORY_CODE = 'NOTICE_CATEGORY';
const FOURM_TYPE_CODE = '4M_TYPE';
const FOURM_STATUS_CODE = '4M_STATUS';
const APP_STATUS_CODE = 'APP_STATUS';
const CONFIRM_STATUS_CODE = 'CONFIRM_STATUS';
const YES_NO_CODE = 'YESNO';
const SOURCE_CODE = 'SOURCE';
const YN_CODE = 'YN';
const FOURM_STATUS = 'FOURM_STATUS';
const PURCHASE_CODE = 'Purchase_Export';

@Injectable({
  providedIn: 'root'
})
export class CommonCodeService implements OnInit {
  private _apiUrl: string;
  private _codes: Array<ICommonCode> = [];

  constructor(
    @Inject(HttpClient) protected http: HttpClient,
    @Inject('BASE_URL') baseUrl: string,
    private logger: NGXLogger) {
    this._apiUrl = `${baseUrl}api/CommonCode`;
  }

  ngOnInit() {
    this.get()
      .subscribe(res => {
        this._codes = res as Array<ICommonCode>;
      });
  }

  get() {
    return this.http.get(this._apiUrl)
      .pipe(handleResponse<Array<ICommonCode>>(this.logger, 'retrieve common code'));
  }

  getCodes(code: string) {
    // cache 데이터를 확인한 다음, 찾지 못한 경우에만 end-point를 경유해서 데이터를 반환
    const codeIndex = this._codes.findIndex(c => c.code.toLowerCase() == code.toLowerCase());
    if (this._codes.length > 0 && codeIndex >= 0) {
      return of(this._codes[codeIndex].details);
    } else {
      return this.http.get(`${this._apiUrl}/${code}`)
        .pipe(handleResponse<Array<ICommonCodeDetail>>(this.logger, `retrieve common code of ${code}`));
    }
  }

  getSiteTypes() {
    return this.getCodes(SITE_TYPE);
  }

  get4mTypes() {
    return this.getCodes(FOURM_TYPE_CODE);
  }

  get4mStatuses() {
    return this.getCodes(FOURM_STATUS_CODE);
  }

  getAppStatuses() {
    return this.getCodes(APP_STATUS_CODE);
  }

  getConfirmStatuses() {
    return this.getCodes(CONFIRM_STATUS_CODE);
  }

  getProgramGroups() {
    return this.getCodes(GROUP_CODE);
  }

  getProgramTypes() {
    return this.getCodes(TYPE_CODE);
  }

  getFourmStatus() {
    return this.getCodes(FOURM_STATUS);
  }

  getYesNo() {
    return this.getCodes(YES_NO_CODE)
      .pipe(
        tap(results => results
          // 예, 아니오 순으로 출력 순서를 변경.
          // 사실, CommonCode에서 Order를 설정하면 될 일인데..
          .sort((prev: ICommonCodeDetail, next: ICommonCodeDetail) => {
            return -(prev.value.localeCompare(next.value));
          }))
      );
  }

  getNoticeCategories() {
    return this.getCodes(NOTICE_CATEGORY_CODE);
  }

  getSource() {
    return this.getCodes(SOURCE_CODE);
  }

  getYN() {
    return this.getCodes(YN_CODE)
      .pipe(
        tap(results => results
          // 예, 아니오 순으로 출력 순서를 변경.
          // 사실, CommonCode에서 Order를 설정하면 될 일인데..
          .sort((prev: ICommonCodeDetail, next: ICommonCodeDetail) => {
            return -(prev.value.localeCompare(next.value));
          }))
      );
  }

  getPurchaseExport() {
    return this.getCodes(PURCHASE_CODE);
  }

  create(code: ICommonCode) {
    return this.http.post(this._apiUrl, code)
      .pipe(handleResponse<ICommonCode>(this.logger, 'create common code'));
  }

  update(code: ICommonCode) {
    return this.http.put(`${this._apiUrl}/${code.code}`, code)
      .pipe(handleResponse<ICommonCode>(this.logger, 'update common code'));
  }

  delete(code: ICommonCode) {
    return this.http.delete(`${this._apiUrl}/${code.code}`)
      .pipe(handleResponse<ICommonCode>(this.logger, 'delete common code'));
  }

  createDetail(detail: ICommonCodeDetail) {
    return this.http.post(`${this._apiUrl}/Detail`, detail)
      .pipe(handleResponse<ICommonCode>(this.logger, 'create common code detail'));
  }

  updateDetail(detail: ICommonCodeDetail) {
    const param = { code: detail.code, value: detail.value };
    return this.http.put(`${this._apiUrl}/Detail`, detail, { params: param })
      .pipe(handleResponse<ICommonCode>(this.logger, 'update common code detail'));
  }
  deleteDetail(detail: ICommonCodeDetail) {
    const param = { code: detail.code, value: detail.value };
    return this.http.delete(`${this._apiUrl}/Detail`, { params: param })
      .pipe(handleResponse<ICommonCode>(this.logger, 'update common code detail'));
  }
}

export interface ICommonCode {
  code: string,
  name: string,
  description: string,
  details: Array<ICommonCodeDetail>,
}

export interface ICommonCodeDetail {
  code: string,
  value: string,
  description: string,
  attr01: string,
  attr02: string,
  attr03: string,
  attr04: number,
  attr05: number,
}
